import * as React from 'react'
import { Checkbox } from '~/client/components/ui/checkbox'
import { Label } from '~/client/components/ui/label'
import { cn } from '~/shared/common/utils'
import {
  AGREEMENT_TYPE_LABELS,
  agreementTypeLink,
  agreementTypeOpensInNewTab,
} from '~/shared/agreement/agreement.constants'
import type { AgreementType } from '~/shared/agreement/agreement.supabase.schema'

type Props = {
  id?: string
  types: ReadonlyArray<AgreementType>
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  disabled?: boolean
  error?: string
  /** Sizes both the label and error text (e.g. 'text-xs' to match a denser form). Defaults to text-sm. */
  textClassName?: string
}

const renderLink = (type: AgreementType) => {
  const href = agreementTypeLink(type)
  const newTab = agreementTypeOpensInNewTab(type)
  return (
    <a
      key={type}
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className="text-[#8A3DFF] underline hover:text-[#7B2FE6]"
    >
      {AGREEMENT_TYPE_LABELS[type]}
    </a>
  )
}

const renderLabelLinks = (types: ReadonlyArray<AgreementType>): React.ReactNode => {
  const nodes = types.map(renderLink)
  if (nodes.length === 0) return null
  if (nodes.length === 1) return nodes[0]
  if (nodes.length === 2) return <>{nodes[0]} and {nodes[1]}</>
  // Oxford comma joining for 3+.
  const head = nodes.slice(0, -1).flatMap((n, idx) => (idx === 0 ? [n] : [', ', n]))
  return <>{head}, and {nodes[nodes.length - 1]}</>
}

export const AgreementCheckbox: React.FC<Props> = ({ id = 'agreement-checkbox', types, checked, onCheckedChange, disabled, error, textClassName }) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-start gap-2">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          className="mt-0.5"
        />
        <Label htmlFor={id} className={cn('text-sm leading-5 cursor-pointer font-normal', textClassName)}>
          I agree to the {renderLabelLinks(types)}.
        </Label>
      </div>
      {error && <span className={cn('text-sm text-destructive', textClassName)}>{error}</span>}
    </div>
  )
}
